import { Tab, Tabs } from '@material-ui/core';
import React, { FC, ReactElement, useState } from 'react';

import { BasicModal } from 'src/general/components/BasicModal/BasicModal';
import { formatFeatures, formatSpecs } from 'src/pdp/sections/Pdp/pdpHelpers';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { CarFeatures } from '../CarFeatures/CarFeatures';
import { CarSummary } from '../CarSummary/CarSummary';
import styles from './FeatureModal.module.scss';

export const TAB_VALUE_SPECIFICATIONS = 'specifications';
export const TAB_VALUE_FEATURES = 'features';

interface Props {
  carSummary: ReturnType<typeof formatSpecs>['carSummary'];
  carFeatures: ReturnType<typeof formatFeatures>['carFeatures'];
  onClose: () => void;
  initialTab: typeof TAB_VALUE_FEATURES | typeof TAB_VALUE_SPECIFICATIONS;
}

type TabPanelProps = {
  children?: React.ReactNode;
  index: string;
  value: string;
};

export const FeatureModal: FC<Props> = ({ carSummary, carFeatures, initialTab, onClose }) => {
  const [tabIndex, setTabIndex] = useState<string>(initialTab);
  const onChangeTab = (_event: any, newValue: React.SetStateAction<string>) => {
    setTabIndex(newValue);
    pushToDataLayer({
      event: 'pdp_interaction',
      type: 'tab_change',
      value: newValue,
    });
  };

  /*
    Tab panel configurations
  */
  const TabPanel = ({ children, value, index, ...other }: TabPanelProps): ReactElement<TabPanelProps> => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );

  const a11yProps = (index: number) => ({
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  });

  const optionalFeaturesCount = carFeatures.optionalFeatures.length;
  const aftermarketFeaturesCount = carFeatures.aftermarketFeatures.length;
  const standardFeaturesCount = carFeatures.standardFeatures.length;
  const numOfFeatures = aftermarketFeaturesCount + optionalFeaturesCount + standardFeaturesCount;
  const showFeaturesTab = numOfFeatures !== 0;
  const numOfSpecs = Object.values(carSummary).filter((val) => !!val).length;
  const showSpecsTab = numOfSpecs !== 0;

  return (
    <BasicModal
      id="feature-modal"
      isOpen
      onClose={onClose}
      title={
        <Tabs value={tabIndex} onChange={onChangeTab} variant="fullWidth" className={styles.tabContainer}>
          {showFeaturesTab && <Tab label="Features" value={TAB_VALUE_FEATURES} {...a11yProps(0)} />}
          {showSpecsTab && <Tab label="Specs" value={TAB_VALUE_SPECIFICATIONS} {...a11yProps(1)} />}
        </Tabs>
      }
      className={styles.root}
    >
      <div className={styles.contentWrapper}>
        {showFeaturesTab && (
          <TabPanel value={tabIndex} index={TAB_VALUE_FEATURES}>
            <div className={styles.tabItemContainer}>
              <CarFeatures {...carFeatures} isOnlyShowFeatureData isShowAllData />
            </div>
          </TabPanel>
        )}
        {showSpecsTab && (
          <TabPanel value={tabIndex} index={TAB_VALUE_SPECIFICATIONS}>
            <div className={styles.tabItemContainer}>
              <CarSummary {...carSummary} isOnlyShowPdpDetails />
            </div>
          </TabPanel>
        )}
      </div>
    </BasicModal>
  );
};
